import { useEffect, useMemo, useState } from 'react';
import type { AuditAction, AuditEntry } from '../types';
import { listActivity, type ListActivityOpts } from '../lib/api';
import { Skeleton } from '../components/Skeleton';
import { IconRefresh } from '../components/icons';
import { relativeDate } from '../lib/format';

/**
 * Activity log Phase 2, vraie page.
 *
 * Source de vérité : table Airtable ActivityLog (tble5ba0OohYLDVB1) via
 * GET /api/admin/activity. Filtres table/action, pagination simple (limit
 * + offset locaux, pas URL-persistée Phase 2).
 *
 * Diff payload expandable inline via <details> natif HTML, pas de side-panel
 * (cohérent avec le pattern actuel d'admin-site/app, le side-panel viendra
 * dans une PR ultérieure si besoin).
 *
 * Mode dégradé : si l'API renvoie une erreur, bandeau rouge persistant +
 * bouton Refresh. Pas de fallback buffer mémoire ici (le buffer vit
 * côté serveur de la Function, pas côté React).
 */

const ALL_ACTIONS: AuditAction[] = [
  'publish',
  'update',
  'delete',
  'promote',
  'archive',
  'mark_reviewed',
  'reject',
];

const DEFAULT_LIMIT = 50;

export function ActivityLogPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableFilter, setTableFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const opts: ListActivityOpts = {
        limit,
        offset,
      };
      if (tableFilter) opts.table = tableFilter;
      if (actionFilter) opts.action = actionFilter as AuditAction;
      const res = await listActivity(opts);
      setEntries(res.entries);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load activity');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [tableFilter, actionFilter, limit, offset]);

  // Tables distinctes pour le dropdown filter, calculées depuis les entrées
  // chargées. Si le filtre table est appliqué, on dérive depuis un fetch
  // précédent (sinon on aurait une liste vide). Pour Phase 2 c'est suffisant.
  const distinctTables = useMemo(() => {
    const set = new Set(entries.map((e) => e.table).filter(Boolean));
    return Array.from(set).sort();
  }, [entries]);

  function resetFilters() {
    setTableFilter('');
    setActionFilter('');
    setOffset(0);
  }

  const hasNext = entries.length === limit;
  const canPrev = offset > 0;

  return (
    <div className="px-4 md:px-8 py-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-display tracking-tightish text-ink">
            Activity log
          </h1>
          <p className="mt-1 text-[13.5px] text-ink-muted max-w-2xl">
            Journal des mutations admin (publish, update, delete, etc.) avec reviewer, mode et
            payload diff. Source Airtable, table ActivityLog.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resetFilters}
            className="h-9 px-3 rounded-md border border-line bg-white text-[13px] text-ink-muted hover:bg-cream-tint transition"
            title="Effacer les filtres"
          >
            Reset
          </button>
          <button
            onClick={refresh}
            disabled={loading}
            className="h-9 px-3 rounded-md bg-navy text-white text-[13px] font-medium flex items-center gap-1.5 hover:bg-navy-soft transition disabled:opacity-60"
          >
            <IconRefresh className={'w-4 h-4 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          <span className="font-medium">Erreur : </span>{error}
        </div>
      )}

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md border border-line-soft bg-cream p-3 shadow-card">
        <div className="flex flex-col gap-1 min-w-[180px]">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">
            Table
          </label>
          <select
            value={tableFilter}
            onChange={(e) => { setTableFilter(e.target.value); setOffset(0); }}
            className="h-9 rounded-md border border-line bg-white px-2.5 text-[13px]"
          >
            <option value="">Toutes</option>
            {distinctTables.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
            {/* Quand le filtre est appliqué pour la première fois, distinctTables
                peut ne pas contenir la valeur active. On l'ajoute manuellement. */}
            {tableFilter && !distinctTables.includes(tableFilter) && (
              <option value={tableFilter}>{tableFilter}</option>
            )}
          </select>
        </div>
        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">
            Action
          </label>
          <select
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setOffset(0); }}
            className="h-9 rounded-md border border-line bg-white px-2.5 text-[13px]"
          >
            <option value="">Toutes</option>
            {ALL_ACTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1 min-w-[110px]">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">
            Limit
          </label>
          <input
            type="number"
            value={limit}
            onChange={(e) => { setLimit(Math.max(10, Math.min(200, parseInt(e.target.value, 10) || DEFAULT_LIMIT))); setOffset(0); }}
            min={10}
            max={200}
            step={10}
            className="h-9 rounded-md border border-line bg-white px-2.5 text-[13px] num w-24"
          />
        </div>
        <div className="ml-auto text-[12px] text-ink-muted">
          <span className="num">{entries.length}</span> entrées affichées
          {(tableFilter || actionFilter) && (
            <span className="ml-2 text-ink-faint">(filtrées)</span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border border-line-soft bg-cream shadow-card overflow-hidden">
        {loading && entries.length === 0 ? (
          <LoadingSkeleton />
        ) : entries.length === 0 ? (
          <EmptyState hasFilters={Boolean(tableFilter || actionFilter)} hasError={Boolean(error)} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[11px] uppercase tracking-wider text-ink-faint font-medium bg-cream-tint border-b border-line-soft">
                <tr>
                  <th className="px-3 py-2 text-left w-44">Quand</th>
                  <th className="px-3 py-2 text-left">Reviewer</th>
                  <th className="px-3 py-2 text-left">Action</th>
                  <th className="px-3 py-2 text-left">Table</th>
                  <th className="px-3 py-2 text-left">Record</th>
                  <th className="px-3 py-2 text-left">Mode</th>
                  <th className="px-3 py-2 text-left w-16">Diff</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-soft">
                {entries.map((e) => (
                  <Row key={e.id} entry={e} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination footer */}
        {(canPrev || hasNext) && (
          <div className="flex items-center justify-between gap-3 border-t border-line-soft bg-cream-tint px-4 py-2 text-[12px] text-ink-muted">
            <span>Offset <span className="num">{offset}</span></span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={!canPrev}
                className="px-3 py-1 rounded border border-line bg-white text-[12px] text-ink-muted disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cream-tint transition"
              >
                ‹ Précédent
              </button>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={!hasNext}
                className="px-3 py-1 rounded border border-line bg-white text-[12px] text-ink-muted disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cream-tint transition"
              >
                Suivant ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ entry }: { entry: AuditEntry }) {
  const hasDiff = entry.payloadDiff && Object.keys(entry.payloadDiff).length > 0;
  return (
    <>
      <tr className="hover:bg-cream-tint">
        <td className="px-3 py-2 text-ink-muted text-[12px] num whitespace-nowrap">
          {relativeDate(entry.timestamp)}
        </td>
        <td className="px-3 py-2 text-ink">
          {entry.reviewer || <span className="text-ink-faint italic">inconnu</span>}
        </td>
        <td className="px-3 py-2">
          <ActionBadge action={entry.action} />
        </td>
        <td className="px-3 py-2 text-ink-muted font-mono text-[12px]">{entry.table}</td>
        <td className="px-3 py-2 text-ink-faint font-mono text-[12px] truncate max-w-[12rem]">
          {entry.recordId || '-'}
        </td>
        <td className="px-3 py-2">
          <ModeBadge mode={entry.mode} secretValid={entry.secretValid} />
        </td>
        <td className="px-3 py-2 text-right">
          {hasDiff ? (
            <span className="text-[11px] text-founders-deep">{Object.keys(entry.payloadDiff!).length} champs</span>
          ) : (
            <span className="text-[11px] text-ink-faint">-</span>
          )}
        </td>
      </tr>
      {hasDiff && (
        <tr className="bg-cream-tint/60">
          <td colSpan={7} className="px-3 pb-2 pt-0">
            <details>
              <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-ink-faint font-medium py-1">
                Voir le payload diff ({Object.keys(entry.payloadDiff!).length} champs)
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-navy text-white/85 p-3 text-[11px] font-mono leading-5">
                {JSON.stringify(entry.payloadDiff, null, 2)}
              </pre>
              {entry.note && (
                <p className="mt-2 text-[12px] text-ink-muted">
                  <span className="font-medium">Note :</span> {entry.note}
                </p>
              )}
            </details>
          </td>
        </tr>
      )}
    </>
  );
}

function ActionBadge({ action }: { action: AuditEntry['action'] }) {
  const palette: Record<AuditEntry['action'], string> = {
    publish: 'bg-funds-soft text-funds-deep',
    update: 'bg-founders-soft text-founders-deep',
    delete: 'bg-red-50 text-red-700',
    promote: 'bg-funds-soft text-funds-deep',
    archive: 'bg-amber-soft text-amber',
    mark_reviewed: 'bg-cream-tint text-ink-muted',
    reject: 'bg-red-50 text-red-700',
  };
  return (
    <span className={'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ' + palette[action]}>
      {action}
    </span>
  );
}

function ModeBadge({ mode, secretValid }: { mode: AuditEntry['mode']; secretValid: boolean }) {
  const base = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium';
  if (mode === 'live') {
    return (
      <span
        className={base + ' bg-funds-soft text-funds-deep'}
        title={secretValid ? 'Identity validée' : 'Identity manquante'}
      >
        live{secretValid ? '' : '*'}
      </span>
    );
  }
  if (mode === 'simulation') {
    return <span className={base + ' bg-amber-soft text-amber'}>simulation</span>;
  }
  return <span className={base + ' bg-cream-tint text-ink-muted'}>{mode}</span>;
}

function LoadingSkeleton() {
  return (
    <div className="p-3 space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}

function EmptyState({ hasFilters, hasError }: { hasFilters: boolean; hasError: boolean }) {
  if (hasError) {
    return (
      <div className="p-10 text-center">
        <p className="text-[13.5px] text-ink font-medium">Impossible de charger l&apos;activity log.</p>
        <p className="mt-1 text-[12px] text-ink-muted max-w-md mx-auto">
          Vérifie que la table ActivityLog est bien configurée (env <span className="font-mono">AIRTABLE_TABLE_ACTIVITY_LOG</span>)
          et que le token Airtable a accès à la base.
        </p>
      </div>
    );
  }
  if (hasFilters) {
    return (
      <div className="p-10 text-center">
        <p className="text-[13.5px] text-ink font-medium">Aucune entrée pour ces filtres.</p>
        <p className="mt-1 text-[12px] text-ink-muted">Essaye de réinitialiser ou d&apos;élargir les critères.</p>
      </div>
    );
  }
  return (
    <div className="p-10 text-center">
      <p className="text-[13.5px] text-ink font-medium">Aucune mutation enregistrée pour le moment.</p>
      <p className="mt-1 text-[12px] text-ink-muted max-w-md mx-auto">
        Le log se remplit dès qu&apos;un endpoint /api/admin/* (funds, news, publish) écrit dans Airtable.
        Fais une modification depuis Investors directory ou News pour voir apparaître la première entrée.
      </p>
    </div>
  );
}
